import React, { useState } from 'react';
import { Loader2, ClipboardPaste, CheckCircle2, AlertTriangle, Search, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { adminAPI } from '../services/api';

const PLACEHOLDER = `MERCEDES-BENZ Citan Van (W415) (Année de construction 11.2012 - 08.2021)
108 CDI (415.603), Année de construction 05.2019 - 08.2021, 1461 ccm, 80 CV
109 CDI (415.603, 415.605), Année de construction 05.2019 - 08.2021, 1461 ccm, 95 CV
RENAULT Kangoo II Express (FW0/1_) (Année de construction 02.2008 - ...)
1.5 dCi (FW0B), 1461 ccm, 86 CV`;

const Badge = ({ ok, children }) => (
  <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full ${ok ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
    {ok ? <CheckCircle2 size={11} /> : <AlertTriangle size={11} />}{children}
  </span>
);

export const CompatPasteModal = ({ open, onClose, hasExisting, onApply, bulkCount = 0, aiSuggest = null }) => {
  const [aiBusy, setAiBusy] = useState(false);
  const [aiNote, setAiNote] = useState('');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [mode, setMode] = useState('merge');
  const [applying, setApplying] = useState(false);

  const analyse = async () => {
    setBusy(true);
    try {
      const r = await adminAPI.parseCompat(text);
      setResult(r);
      if (!r.groups.length) toast.error('Aucun véhicule reconnu dans le texte collé');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Analyse impossible');
    } finally {
      setBusy(false);
    }
  };

  const close = () => { setText(''); setResult(null); setAiNote(''); onClose(); };
  const askAi = async () => {
    setAiBusy(true);
    try {
      const r = await aiSuggest();
      setAiNote(r.note || '');
      if (!r.text) { toast.info(r.note || 'Aucune proposition'); return; }
      setText(r.text);
      if (r.result) setResult(r.result);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Proposition IA impossible');
    } finally {
      setAiBusy(false);
    }
  };
  const apply = async () => {
    setApplying(true);
    try { await onApply(result, mode, text); close(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Application impossible'); }
    finally { setApplying(false); }
  };
  const s = result?.stats;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="compat-paste-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ClipboardPaste size={18} className="text-orange-500" /> Coller une liste de véhicules compatibles{bulkCount > 0 && <span className="text-sm font-normal text-gray-500">· {bulkCount} produit(s) sélectionné(s)</span>}</DialogTitle>
          <DialogDescription>Collez la liste telle qu'elle apparaît sur la fiche fournisseur (marque + modèle, puis une ligne par motorisation). Les véhicules sont retrouvés automatiquement dans la base et affichés sur la fiche produit.</DialogDescription>
        </DialogHeader>

        {aiNote && <p className="text-xs text-violet-800 bg-violet-50 border border-violet-200 rounded-md px-3 py-2 flex items-center gap-2" data-testid="compat-paste-ai-note"><Sparkles size={13} /> {aiNote} Vérifiez avant d'ajouter.</p>}
        {!result ? (
          <textarea data-testid="compat-paste-textarea" value={text} onChange={(e) => setText(e.target.value)} rows={12} placeholder={PLACEHOLDER}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-orange-500" />
        ) : (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2 text-sm" data-testid="compat-paste-stats">
              <Badge ok={s.models_matched === s.vehicles}>{s.models_matched}/{s.vehicles} véhicule(s) retrouvé(s) dans la base</Badge>
              <Badge ok={s.engines_matched === s.engines}>{s.engines_matched}/{s.engines} motorisation(s) reconnue(s)</Badge>
            </div>
            <div className="border rounded-lg divide-y max-h-[45vh] overflow-y-auto">
              {result.groups.map((g, i) => (
                <div key={i} className="p-3" data-testid={`compat-paste-group-${i}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-sm text-gray-900">{g.brand}</span>
                    <span className="text-sm text-gray-800">{g.model}</span>
                    {g.model_years && <span className="text-xs text-gray-500">{g.model_years}</span>}
                    {g.model_matched
                      ? <Badge ok>{g.model !== g.model_raw ? `reconnu (« ${g.model_raw} »)` : 'reconnu'}</Badge>
                      : <Badge ok={false}>{g.brand_matched ? 'modèle inconnu dans la base — gardé en texte' : 'marque inconnue — gardée en texte'}</Badge>}
                  </div>
                  {g.engines.length > 0 && (
                    <ul className="mt-1.5 flex flex-wrap gap-1.5">
                      {g.engines.map((e, j) => (
                        <li key={j} title={e.raw} className={`text-[11px] px-2 py-0.5 rounded-full ${e.matched ? 'bg-[#1e3a5f] text-white' : 'bg-gray-100 text-gray-700 border border-dashed border-gray-400'}`}>
                          {e.engine}{e.engine_years ? <span className="opacity-70"> · {e.engine_years}</span> : null}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
            {s.unmatched_models.length > 0 && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="compat-paste-unmatched">
                Non trouvés dans la base véhicules : {s.unmatched_models.join(' · ')}. Ils seront conservés en texte et listés dans Admin › Véhicules › « Modèles collés non reconnus » pour les ajouter ou les associer.
              </p>
            )}
            <p className="text-xs text-gray-500">Bleu : motorisation exacte de la base véhicules (le filtre par véhicule/plaque fonctionnera). Pointillé : conservée telle quelle pour l'affichage.</p>
            {(hasExisting || bulkCount > 0) && (
              <div className="flex gap-4 text-sm">
                <label className="flex items-center gap-1.5"><input type="radio" checked={mode === 'merge'} onChange={() => setMode('merge')} data-testid="compat-paste-mode-merge" /> Ajouter à la liste existante{bulkCount > 0 ? ' de chaque produit' : ''}</label>
                <label className="flex items-center gap-1.5"><input type="radio" checked={mode === 'replace'} onChange={() => setMode('replace')} data-testid="compat-paste-mode-replace" /> Remplacer la liste existante{bulkCount > 0 ? ' de chaque produit' : ''}</label>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          {aiSuggest && !result && (
            <Button type="button" variant="outline" onClick={askAi} disabled={aiBusy || busy} className="border-violet-300 text-violet-700 hover:bg-violet-50 mr-auto" data-testid="compat-paste-ai" title="L'IA propose les véhicules compatibles d'après la référence et les numéros OE ; la liste est ensuite vérifiée dans la base">
              {aiBusy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Sparkles size={16} className="mr-2" />} Proposer par IA
            </Button>
          )}
          {result && <Button type="button" variant="outline" onClick={() => setResult(null)} data-testid="compat-paste-back">Modifier le texte</Button>}
          {!result ? (
            <Button type="button" onClick={analyse} disabled={busy || text.trim().length < 3} className="bg-orange-500 hover:bg-orange-600" data-testid="compat-paste-analyse">
              {busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Search size={16} className="mr-2" />} Analyser
            </Button>
          ) : (
            <Button type="button" onClick={apply} disabled={!result.groups.length || applying} className="bg-green-600 hover:bg-green-700" data-testid="compat-paste-apply">
              {applying ? <Loader2 size={16} className="mr-2 animate-spin" /> : <CheckCircle2 size={16} className="mr-2" />} {bulkCount > 0 ? `Appliquer ${result.fitments.length} ligne(s) à ${bulkCount} produit(s)` : `Ajouter ${result.fitments.length} ligne(s) au produit`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CompatPasteModal;
